import React from 'react';
import type { RegisterSession, Order, InventoryItem, Recipe } from '../../types';
import RegisterControl from './RegisterControl';
import SessionSelector from './SessionSelector';
import SharedRegisterChip from '../common/SharedRegisterChip';
import LiveDataStatusBadge from '../common/LiveDataStatusBadge';
import SpeedTestButton from '../common/SpeedTestButton';
import NotificationBell from '../common/NotificationBell';

interface POSHeaderProps {
  registerSession: RegisterSession | undefined;
  activeSessions?: RegisterSession[];
  terminalId?: string;
  terminalName?: string;
  onTerminalNameChange: (name: string) => void;
  todaysOrders: Order[];
  sessionOrders?: Order[]; // Orders from when the register session started
  inventoryItems?: InventoryItem[];
  recipes?: Recipe[];
  onOpenRegister: (openingFloat: number) => void;
  onCloseRegister: (
    closingFloat: number,
    session: RegisterSession,
    todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
  ) => void;
  onStockTake?: (physicalCounts: { [itemId: number]: number }) => void;
  onForceCloseSession?: (session: RegisterSession) => void;
  onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
  currentUser?: any; // StaffMember or CurrentUser
  showForceClose?: boolean;
  onUpdateOrder?: (order: Order) => void | Promise<void>;
  onMarkAsPaid?: (orderId: string) => void | Promise<void>;
}

const POSHeader: React.FC<POSHeaderProps> = ({ 
  registerSession, 
  activeSessions = [],
  terminalId = '',
  terminalName = '',
  onTerminalNameChange,
  todaysOrders,
  sessionOrders,
  inventoryItems = [],
  recipes = [],
  onOpenRegister, 
  onCloseRegister,
  onStockTake,
  onForceCloseSession,
  onRequireManagerPin,
  currentUser,
  showForceClose = false,
  onUpdateOrder,
  onMarkAsPaid,
}) => {
  return (
    <header className="bg-white shadow-sm z-10 flex-shrink-0 border-b border-gray-200">
      <div className="max-w-full mx-auto px-2 sm:px-3 h-10 sm:h-12 flex items-center justify-between gap-2 sm:gap-3">
        {/* Terminal, User & Register Control (left side - avoids accidental Force Close when maximizing) */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs">
            <span className="font-bold text-gray-800 truncate">{terminalName}</span>
            <span className="text-gray-400 hidden sm:inline">|</span>
            {registerSession ? (
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-semibold text-gray-700 truncate">{registerSession.cashierName}</span>
              </div>
            ) : (
              <span className="text-gray-500">No session</span>
            )}
            <span className="text-gray-300 hidden sm:inline">|</span>
            <RegisterControl 
            session={registerSession}
            todaysOrders={todaysOrders}
            sessionOrders={sessionOrders}
            inventoryItems={inventoryItems}
            recipes={recipes}
            onOpenRegister={onOpenRegister}
            onCloseRegister={onCloseRegister}
            onStockTake={onStockTake}
            onForceCloseSession={onForceCloseSession}
            onRequireManagerPin={onRequireManagerPin}
            currentUser={currentUser}
            showForceClose={showForceClose}
            onUpdateOrder={onUpdateOrder}
            onMarkAsPaid={onMarkAsPaid}
          />
            {/* Shared-register chip lives next to the close-register control so
                the register UI stays clustered on the left side of the bar,
                clear of the top-right area. */}
            <SharedRegisterChip
              session={registerSession}
              currentUser={currentUser}
            />
            {/* Live-data connection health badge, parked beside the register
                chip so all the operational status indicators read together. */}
            <LiveDataStatusBadge variant="inline" />
            <SpeedTestButton />
            {/* Staff alerts: catering requests, app chat, feedback, meal-plan exceptions. */}
            <NotificationBell />
          </div>
        </div>
      </div>
    </header>
  );
};

export default POSHeader;